import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Download, BookOpen, Link, Edit } from 'lucide-react';
import axios from 'axios';
import DonorTable, { RecipientTable, EpletTable } from './tables';
import './App.css';
import { DonorRecipientRanking } from './ranking';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Button, TextField, Autocomplete, IconButton, Tabs, Tab, Box, Paper, Typography, Stack, Chip, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { API_BASE_URL } from './config';

import AlignmentTab from './Tabs/AlignmentTab';
import SASTab from './Tabs/SASTab';
import MismatchTab from './Tabs/MismatchTab';
import EpletTab from './Tabs/EpletTab';
import RankingTab from './Tabs/RankingTab';
import ContactSection from './ContactSection';


export const DocLink = ({ href, children }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="doc-link"
  >
    {children}
    <Link size={14} style={{ marginLeft: '4px', verticalAlign: 'middle' }} />
  </a>
);


const StyledTab = styled(Tab)(({ theme }) => ({

  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: 2,
    backgroundColor: 'transparent',
    transition: 'background-color 0.3s',
  },
  '&:hover': {
    color: theme.palette.primary.light,
    opacity: 1,
    backgroundColor: 'transparent',
    '&::after': {
      backgroundColor: theme.palette.primary.light,
    },
  },
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    '&::after': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

let allelesCache = null;

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

// Function to check if the created data is empty
const isEmptyCreatedData = (data) => {
  // check if all the donors alleles and recipients alleles are empty
  return data.every(d => d.alleles.length === 0);
};

function AlternativeLayout() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [inputMethod, setInputMethod] = useState('file');
  const [isLoading, setIsLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [executionTime, setExecutionTime] = useState(null);
  const [fileName, setFileName] = useState('No file selected');
  const [entityInfo, setEntityInfo] = useState(null);
  const [rsa, setRsa] = useState('0.25');
  const [rsaError, setRsaError] = useState(null);
  const [inputSubmissionStatus, setInputSubmissionStatus] = useState(null);
  const [inputErrorMessage, setInputErrorMessage] = useState(null);
  const [donors, setDonors] = useState([{ id: 'Donor1', alleles: [] }]);
  const [recipients, setRecipients] = useState([{ id: 'Recipient1', alleles: [] }]);
  const [alleles, setAlleles] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const [classesToShow, setClassesToShow] = useState([]);

  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobQueuePosition, setJobQueuePosition] = useState(null);
  

  // Add state variables for each tab
  const [AlignmentTabData, setAlignmentTabData] = useState({});
  const [SAScoresTabData, setSAScoresTabData] = useState({});
  const [MismatchesTabData, setMismatchesTabData] = useState({});
  const [EpletTabData, setEpletTabData] = useState({});
  const [RankingTabData, setRankingTabData] = useState({});
  const [epletFilter, setEpletFilter] = useState('confirmed');
  const [useExon4, setUseExon4] = useState(false);


  const pollForResults = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/poll_results/${id}`);
      if (response.data.status === 'completed') {
        setResponseData(response.data.result);
        setEntityInfo(response.data.result.entity_info);
        setExecutionTime(response.data.result.execution_time);
        setInputSubmissionStatus("success");
        setClassesToShow(response.data.result.classes_to_show);
        setIsLoading(false);
        setJobId(null);
        setJobStatus(null);
        setJobQueuePosition(null);
      } else if (response.data.status === 'processing') {
        setJobStatus('active');
        setTimeout(() => pollForResults(id), 2000);
      } else if (response.data.status === 'queued') {
        setJobStatus('queued');
        setJobQueuePosition(response.data.queue_position);
        setTimeout(() => pollForResults(id), 2000);

      } else if (response.data.status === 'error') {
        setInputSubmissionStatus("error");
        console.log("response data", response.data);
        setInputErrorMessage(response.data.error || "An error occurred while processing your request.");
        setIsLoading(false);
        setJobId(null);
        setJobStatus(null);
        setJobQueuePosition(null);
      } else {
        throw new Error('Unexpected status');
      }
    } catch (error) {
      console.error('Error polling for results:', error);
      setInputSubmissionStatus("error");
      if (error.response?.status == 503) {
        setInputErrorMessage("The server is currently busy. Please try again later.");
      } else {
        setInputErrorMessage(error.response?.data?.result?.error || error.message || "An error occurred while retrieving results. Please try again.");
      }
      setIsLoading(false);
      setJobId(null);
      setJobStatus(null);
      setJobQueuePosition(null);
    }
  }, []);

  useEffect(() => {
    if (jobId) {
      pollForResults(jobId);
    }
  }, [jobId, pollForResults]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchAlleles = useCallback(async () => {
    if (allelesCache) {
      setAlleles(allelesCache);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/allele_ids`);
      allelesCache = response.data;
      setAlleles(allelesCache);
      console.log(response.data)
    } catch (error) {
      console.error('Error fetching alleles:', error);
    }
  }, []);

  useEffect(() => {
    fetchAlleles();
  }, [fetchAlleles]);

  useEffect(() => {
    if (inputMethod === 'create' && donors.length === 0 && recipients.length === 0) {
      addDonor();
      addRecipient();
    }
  }, [inputMethod]);
  
  useEffect(() => {
    console.log('Current API base URL:', API_BASE_URL);
  }, []);

  const getNextAvailableId = (prefix, currentList) => {
    const usedNumbers = currentList.map(item => 
      parseInt(item.id.replace(prefix, ''))
    );
    for (let i = 1; i <= currentList.length + 1; i++) {
      if (!usedNumbers.includes(i)) {
        return `${prefix}${i}`;
      }
    }
  };

  const addDonor = () => {
    const newId = getNextAvailableId('Donor', donors);
    setDonors([...donors, { id: newId, alleles: [] }]);
  };

  const addRecipient = () => {
    const newId = getNextAvailableId('Recipient', recipients);
    setRecipients([...recipients, { id: newId, alleles: [] }]);
  };

  

  const handleRsaChange = (event) => {
    const value = event.target.value.replace(',', '.');
    
    // Allow empty input for user convenience while typing
    if (value === '') {
      setRsa(value);
      setRsaError(null);
      return;
    }

    // Check if the input is a valid number between 0 and 1
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 1 && /^\d*\.?\d*$/.test(value)) {
      setRsa(value);
      setRsaError(null);
    } else {
      setRsa(value);  // Still update the input for UX, but set an error
      setRsaError("RSA must be a number between 0 and 1");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    } else {
      setSelectedFile(null);
      setFileName('No file selected');
    }
  };
  
  const handleInputMethodChange = (value) => {
    setInputMethod(value);
    if (value === 'create' && donors.length === 0 && recipients.length === 0) {
      addDonor();
      addRecipient();
    }
  };

  const handleSubmit = async () => {
    if (rsaError || rsa === '' || isNaN(parseFloat(rsa))) {
      setInputSubmissionStatus("error");
      setInputErrorMessage("Please enter a valid RSA value between 0 and 1.");
      return;
    }

    setIsLoading(true);
    setInputSubmissionStatus(null);
    setInputErrorMessage(null);
    setResponseData(null); // Reset the response data when starting a new submission
    // resest the tab states
    setAlignmentTabData({});
    setSAScoresTabData({});
    setMismatchesTabData({});

    const formData = new FormData();
    
    if (inputMethod === 'file' && selectedFile) {
      formData.append('file', selectedFile);
    } else if (inputMethod === 'create') {
      const data = {
        donors: donors.map(donor => ({ identifier: donor.id, type: "Donor", alleles: donor.alleles })),
        recipients: recipients.map(recipient => ({ identifier: recipient.id, type: "Recipient", alleles: recipient.alleles }))
      };
      formData.append('created_data', JSON.stringify(data));
    }

    formData.append('rsa', parseFloat(rsa));  // Ensure we send a number
    formData.append('eplet_filter', epletFilter); // Add this line
    formData.append('use_exon4', useExon4); // Add this line

    try {
      const response = await axios.post(`${API_BASE_URL}/job_submission`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'queued') {
        setJobId(response.data.id);
        setJobStatus('queued');
        setInputSubmissionStatus("pending");
        // setInputErrorMessage("Your request has been queued. Please wait...");
        console.log("Request queued with ID:", response.data.id);
      } else {
        // Handle immediate response (unlikely with the new setup)
        setResponseData(response.data);
        setEntityInfo(response.data.entity_info);
        setExecutionTime(response.data.execution_time);
        setInputSubmissionStatus("success");
        setClassesToShow(response.data.classes_to_show);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setInputSubmissionStatus("error");
      setInputErrorMessage(error.response?.data?.detail || error.message || "An unknown error occurred");
      setIsLoading(false);
    }
  };

  const theme = createTheme({
    components: {
      MuiAutocomplete: {
        styleOverrides: {
          popper: {
            zIndex: 9999,
          },
        },
      },
    },
  });

  const handleExport = async () => {
    if (responseData) {
      const zip = new JSZip();

      const excelFolder = zip.folder("results_excel");
      const csvFolder = zip.folder("results_csv");

      // Add the excel files to the zip
      const export_files = responseData.output_files
      console.log("export files", export_files)
      excelFolder.file("Alignment.xlsx", export_files.alignment.excel, {base64: true});
      excelFolder.file("Input.xlsx", export_files.input.excel, {base64: true});
      excelFolder.file("Mismatches.xlsx", export_files.mismatches.excel, {base64: true});
      excelFolder.file("SAS_scores.xlsx", export_files.sas_scores.excel, {base64: true});
      excelFolder.file("KnownEplets.xlsx", export_files.eplets.excel, {base64: true});

      // Add the csv files to the zip
      // loop over the keys of export_files.alignment.csv
      for (const key in export_files.alignment.csv) {
        csvFolder.file("Alignment_" + key + ".csv", export_files.alignment.csv[key], {base64: true});
      }
      for (const key in export_files.input.csv) {
        csvFolder.file("Input_" + key + ".csv", export_files.input.csv[key], {base64: true});
      }
      for (const key in export_files.mismatches.csv) {
        csvFolder.file("Mismatches_" + key + ".csv", export_files.mismatches.csv[key], {base64: true});
      }
      for (const key in export_files.sas_scores.csv) {
        csvFolder.file("SAS_scores_" + key + ".csv", export_files.sas_scores.csv[key], {base64: true});
      }
      for (const key in export_files.eplets.csv) {
        csvFolder.file("KnownEplets_" + key + ".csv", export_files.eplets.csv[key], {base64: true});
      }

      zip.generateAsync({ type: "blob" })
        .then(function (content) {
          saveAs(content, "hmc_matchmaker_results.zip");
        });
    }
  };


  // filter options for autocomplete
  const filterOptions = createFilterOptions({
    limit: 2000,
    ignoreCase: true,
    matchFrom: 'start',
  });

  return (
    <ThemeProvider theme={theme}>
      <div className="container-wide">
        <Box sx={{ 
  display: 'flex', 
  justifyContent: 'space-between', 
  alignItems: 'center',
  py: 3,
  mb: 2,
  // Completely override any inherited styles
  borderBottom: 'none',
  borderTop: 'none',
  boxShadow: 'none',
  '&::after': { display: 'none' },
  '&::before': { display: 'none' }
}}>
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    <Box sx={{ 
      width: 48, 
      height: 48, 
      borderRadius: '12px',
      background: 'linear-gradient(135deg, #1976d2, #42a5f5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '1.5rem'
    }}>
      🧬
    </Box>
    <Box>
      <Typography 
        variant="h4" 
        component="h1"
        sx={{
          fontWeight: 600,
          color: '#1976d2',
          lineHeight: 1.2,
          fontSize: { xs: '1.5rem', md: '2rem' },
          margin: 0,
          padding: 0
        }}
      >
        MHC Matchmaker
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: 'text.secondary',
          fontSize: '0.875rem',
          margin: 0,
          padding: 0
        }}
      >
        MHC compatibility on the Amino Acid level
      </Typography>
    </Box>
  </Box>
  <Button 
    variant="outlined" 
    startIcon={<BookOpen size={18} />} 
    href={`${API_BASE_URL}/docs/index.html`} 
    target="_blank" 
    rel="noopener noreferrer"
    sx={{ 
      textTransform: 'none',
      borderRadius: 2
    }}
  >
    Documentation
  </Button>
</Box>

        {/* Homepage Introduction Section */}
        <StyledPaper elevation={3}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <StyledTypography variant="h4" component="h2" gutterBottom>
              Welcome to MHC Matchmaker
            </StyledTypography>
            {/* <Typography variant="h6" color="text.secondary" gutterBottom>
              Advanced MHC Compatibility Analysis for Transplantation
            </Typography> */}
          </Box>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
            MHC Matchmaker is a validated computational tool for assessing histocompatibility at the amino acid level across species. 
            Developed to address the growing need for cross-species MHC analysis in xenotransplantation research and non-human primate studies,
            our platform enables precise compatibility assessment through detailed protein sequence analysis.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mt: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom color="primary">
                🔬 Core Capabilities
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3 }}>
                <Typography component="li" variant="body2" paragraph>
                  <strong>Cross-Species Compatibility:</strong>  Assess MHC compatibility between human (HLA), pig (SLA), rhesus macaque (Mamu), and cynomolgus macaque (Mafa) donors and recipients
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  <strong>Amino Acid-Level Analysis:</strong> Identify functionally relevant sequence differences at positions critical for immune recognition
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  <strong>Structural Accessibility Filtering:</strong> Use Relative Solvent Accessibility (RSA) scores with adjustable thresholds (default 0.25) to focus on surface-exposed, immunologically relevant mismatches
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  <strong>Known Eplet Detection:</strong> Identify confirmed immunogenic epitopes from the HLA Eplet Registry
                </Typography>
                <Typography component="li" variant="body2" paragraph>
                  <strong>Weighted Mismatch Scoring:</strong> Account for allele frequency and immunogenic potential of each mismatch position
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom color="primary">
                ⚙️ Analysis Workflow
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>1. Sequence Alignment:</strong> Align MHC sequences using consensus references for each class (I, II-DQα, II-DQβ, II-DRα, II-DRβ)
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>2. Mismatch Detection:</strong> Identify amino acid differences between grouped donor and recipient sequences
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>3. Accessibility Filtering:</strong> Apply RSA thresholds to focus on surface-exposed, immunologically relevant positions
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>4. Eplet Analysis:</strong> Map known immunogenic epitopes from curated databases to identified mismatches
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>5. Compatibility Ranking:</strong> Generate comprehensive donor rankings based on weighted mismatch profiles
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>6. Export Results:</strong> Download comprehensive mismatch data and donor rankings for further analysis
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ mt: 4, p: 2, backgroundColor: '#e3f2fd', borderRadius: 2, border: '1px solid #bbdefb' }}>
            <Typography variant="body2" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
              🚀 <strong>New to MHC Matchmaker?</strong> Check out our{' '}
              <Button 
                variant="text" 
                size="small" 
                href="/input-instructions" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
              >
                input format guide
              </Button>
              {' '}and{' '}
              <Button 
                variant="text" 
                size="small" 
                href={`${API_BASE_URL}/docs/index.html`}
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
              >
                documentation
              </Button>
              {' '}to get started quickly.
            </Typography>
          </Box>

        </StyledPaper>

        {/* Input MHC Data section continues here... */}
        
        <StyledPaper elevation={3}>
          <StyledTypography variant="h5" component="h2">
            Input MHC Data
          </StyledTypography>
          
          <Box sx={{ mt: 3 }}>
            {/* Improved Input Method Selection */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
                Choose Input Method
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant={inputMethod === 'file' ? 'contained' : 'outlined'}
                  onClick={() => handleInputMethodChange('file')}
                  startIcon={<Upload size={20} />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    minWidth: 150
                  }}
                >
                  Upload File
                </Button>
                <Button
                  variant={inputMethod === 'create' ? 'contained' : 'outlined'}
                  onClick={() => handleInputMethodChange('create')}
                  startIcon={<Edit size={20} />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    minWidth: 150
                  }}
                >
                  Manual input
                </Button>
              </Stack>
            </Box>

            {inputMethod === 'file' ? (
              <Box sx={{ mb: 3 }}>
                {/* File Upload Section */}
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    borderRadius: 2, 
                    backgroundColor: '#fafafa',
                    border: '2px dashed #e0e0e0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  <Stack spacing={2} alignItems="center">
                    <Box sx={{ textAlign: 'center' }}>
                      <Upload size={48} style={{ color: '#666', marginBottom: '8px' }} />
                      <Typography variant="h6" gutterBottom>
                        Upload the Donor and Recipient haplotypes
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Support for CSV and Excel files (.csv, .xlsx, .xls)
                      </Typography>
                    </Box>
                    
                    <input
                      id="file-upload"
                      type="file"
                      onChange={handleFileUpload}
                      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-upload">
                      <Button
                        component="span"
                        variant="contained"
                        startIcon={<Upload size={18} />}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontSize: '1rem'
                        }}
                      >
                        Choose File
                      </Button>
                    </label>
                    
                    {selectedFile && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        border: '1px solid #e0e0e0',
                        minWidth: 300,
                        justifyContent: 'center'
                      }}>
                        <Box component="span" sx={{ fontSize: '1.2rem' }}>📄</Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {fileName}
                        </Typography>
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setSelectedFile(null);
                            setFileName('No file selected');
                          }}
                          sx={{ ml: 1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Stack>
                </Paper>
                
                {/* Instructions Section */}
                <Box sx={{ mt: 2 }}>
                  <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', border: '1px solid #bbdefb' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box component="span" sx={{ fontSize: '1.2rem' }}>💡</Box>
                      <Typography variant="body2">
                        Need help with file format?{' '}
                        <Button
                          variant="text"
                          size="small"
                          href="/input-instructions"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ 
                            textTransform: 'none', 
                            p: 0, 
                            minWidth: 'auto',
                            textDecoration: 'underline'
                          }}
                        >
                          View formatting guide and examples
                        </Button>
                      </Typography>
                    </Stack>
                  </Paper>
                </Box>
              </Box>
            ) : (
              // Create Data section remains the same but can be improved too
              <div className="create-data-section">
                <h3>Donors</h3>
                  {donors.map((donor, index) => (
                    <div key={index} className="entity-input">
                      <TextField
                        value={donor.id}
                        onChange={(e) => {
                          const newDonors = [...donors];
                          newDonors[index].id = e.target.value;
                          setDonors(newDonors);
                        }}
                        label="Donor Name"
                        variant="outlined"
                        style={{ width: '200px', marginRight: '10px' }}
                      />
                      <Autocomplete
                        multiple
                        options={alleles}
                        value={donor.alleles}
                        renderInput={(params) => <TextField {...params} label="Donor Alleles" />}
                        onChange={(event, newValue) => {
                          const newDonors = [...donors];
                          newDonors[index].alleles = newValue;
                          setDonors(newDonors);
                        }}
                        style={{ flexGrow: 1, marginRight: '10px' }}
                        filterOptions={filterOptions}
                      />
                      <IconButton onClick={() => {
                        const newDonors = donors.filter((_, i) => i !== index);
                        setDonors(newDonors);
                      }} aria-label="delete">
                        <CloseIcon />
                      </IconButton>
                    </div>
                  ))}
                  <Button onClick={() => setDonors([...donors, { id: `Donor${donors.length + 1}`, alleles: [] }])}>
                    Add Donor
                  </Button>

                  <h3>Recipients</h3>
                  {recipients.map((recipient, index) => (
                    <div key={index} className="entity-input">
                      <TextField
                        value={recipient.id}
                        onChange={(e) => {
                          const newRecipients = [...recipients];
                          newRecipients[index].id = e.target.value;
                          setRecipients(newRecipients);
                        }}
                        label="Recipient Name"
                        variant="outlined"
                        style={{ width: '200px', marginRight: '10px' }}
                      />
                      <Autocomplete
                        multiple
                        options={alleles}
                        value={recipient.alleles}
                        renderInput={(params) => <TextField {...params} label="Recipient Alleles" />}
                        onChange={(event, newValue) => {
                          const newRecipients = [...recipients];
                          newRecipients[index].alleles = newValue;
                          setRecipients(newRecipients);
                        }}
                        style={{ flexGrow: 1, marginRight: '10px' }}
                        filterOptions={filterOptions}
                      />
                      <IconButton onClick={() => {
                        const newRecipients = recipients.filter((_, i) => i !== index);
                        setRecipients(newRecipients);
                      }} aria-label="delete">
                        <CloseIcon />
                      </IconButton>
                    </div>
                  ))}
                  <Button onClick={() => setRecipients([...recipients, { id: `Recipient${recipients.length + 1}`, alleles: [] }])}>
                    Add Recipient
                  </Button>
                </div>
              )}

            {/* RSA Section */}
            <Box sx={{ mt: 4, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Analysis Configuration
              </Typography>
              <Stack spacing={3}>
                <TextField
                  label="Relative Solvent Accessibility (RSA) Threshold"
                  type="text"
                  value={rsa}
                  onChange={handleRsaChange}
                  error={!!rsaError}
                  helperText={rsaError || "Enter a value between 0 and 1 (e.g., 0.25)"}
                  sx={{ maxWidth: 400 }}
                  variant="outlined"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useExon4}
                      onChange={(e) => setUseExon4(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Use Exon 4 data for Class I alleles
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Warning: Many alleles will not have exon 4, this may lead to an incorrect alignment.
                      </Typography>
                    </Box>
                  }
                />

          <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 2, border: '1px solid #ffb74d' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              ⚠️ Relative Solvent Accessibility (RSA) Prediction Notice
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
            RSA values are computational predictions that may vary, particularly at exon boundaries. 
            Greatest variation occurs at positions 176-180 when exon 4 is included or excluded from calculations.
            </Typography>
          </Box>
              </Stack>
            </Box>

            {/* Submit Section */}
            <Box sx={{ mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={(inputMethod === 'file' && !selectedFile) || isLoading || (inputMethod === 'create' && isEmptyCreatedData(donors)) || (inputMethod === 'create' && isEmptyCreatedData(recipients))}
                startIcon={isLoading ? <span className="spinner"></span> : <Upload />}
                size="large"
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  fontWeight: 600
                }}
              >
                {isLoading ? "Processing..." : "Start Analysis"}
              </Button>
            </Box>
          </Box>

          {/* Status Messages remain the same */}
          {inputSubmissionStatus === 'error' && (
            <div className="error-message">
              {inputErrorMessage || "An error occurred while processing the file. Please try again."}
            </div>
          )}

          {inputSubmissionStatus === 'pending' && (
            <div className="pending-message">
              {inputErrorMessage}
            </div>
          )}

          {/* Add this new section to display invalid and transformed alleles side by side */}
          {responseData && (responseData.invalid_alleles?.length > 0 || Object.keys(responseData.transformed_alleles || {}).length > 0) && (
            <div className="alleles-info-container">
              {responseData.invalid_alleles?.length > 0 && (
                <div className="invalid-alleles-section">
                  <h4>Invalid Alleles <DocLink href={`${API_BASE_URL}/docs/overview.html#input`}/></h4>
                  <ul>
                    {responseData.invalid_alleles.map((allele, index) => (
                      <li key={index}>{allele}</li>
                    ))}
                  </ul>
                </div>
              )}
              

              {Object.keys(responseData.transformed_alleles || {}).length > 0 && (
                <div className="transformed-alleles-section">
                  <h4>Transformed Alleles <DocLink href={`${API_BASE_URL}/docs/overview.html#input`}/></h4>
                  <ul>
                    {Object.entries(responseData.transformed_alleles).map(([original, transformed], index) => (
                      <li key={index}>{original} → {transformed}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {jobId && (
            <Paper elevation={3} sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#333', fontWeight: 'bold' }}>
                Job Status
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mr: 1 }}>Job ID:</Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', backgroundColor: '#e0e0e0', padding: '2px 6px', borderRadius: '4px' }}>
                    {jobId}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <Typography variant="body1" sx={{ fontWeight: 'medium', mr: 1 }}>Status:</Typography>
                  <Chip
                    label={jobStatus === 'active' ? 'Active' : 'Queued'}
                    color={jobStatus === 'active' ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
                {jobStatus === 'queued' && jobQueuePosition && (
                  <Box display="flex" alignItems="center">
                    <Typography variant="body1" sx={{ fontWeight: 'medium', mr: 1 }}>Queue Position:</Typography>
                    <Chip label={jobQueuePosition} color="primary" size="small" />
                  </Box>
                )}
              </Stack>
            </Paper>
          )}
        </StyledPaper>

        {responseData && !isLoading && <div className="section-separator"></div>}

        {responseData && !isLoading && (
          <div className="response-section">

            <div className='success-message'>
              Results have been successfully generated!
            </div>
            <div className="execution-time-export">
              {executionTime && (
                <div className="execution-time">
                  <p><strong>Runtime:</strong> {executionTime.toFixed(2)} seconds</p>
                </div>
              )}
              <button onClick={handleExport} className="export-button">
                <Download size={18} style={{marginRight: '8px'}} /> Download Results
              </button>
            </div>

              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={activeTab} 
                  onChange={handleTabChange} 
                  aria-label="result tabs"
                  TabIndicatorProps={{
                    sx: { display: 'none' },
                  }}
                >
                  <StyledTab label="Input" />
                  <StyledTab label="Alignment" />
                  <StyledTab label="Solvent Accessibility Scores"/>
                  <StyledTab label="Mismatches" />
                  <StyledTab label="Eplets" />
                  <StyledTab label="Rankings" />
                </Tabs>
              </Box>

              <TabPanel value={activeTab} index={0}>
                {/*  Add an introduction text here                */}
                
                <h3>Donors</h3>
                <DonorTable donors={responseData.donors} classesToShow={classesToShow} />
              <h3>Recipients</h3>
              <RecipientTable recipients={responseData.recipients} classesToShow={classesToShow} />
            </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <AlignmentTab 
                  sequences={responseData.alignment} 
                  donors={responseData.donors}
                  recipients={responseData.recipients}
                  tabState={AlignmentTabData}
                  setTabState={setAlignmentTabData}
                  useExon4={useExon4}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                
                <SASTab 
                  entityInfo={entityInfo} 
                  rsa={rsa} 
                  SAScoresTabData={SAScoresTabData} 
                  setSAScoresTabData={setSAScoresTabData}
                  responseData={responseData}
                  useExon4={useExon4}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <MismatchTab 
                  responseData={responseData} 
                  classesToShow={classesToShow} 
                  MismatchesTabData={MismatchesTabData} 
                  setMismatchesTabData={setMismatchesTabData}
                  useExon4={useExon4}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={4}>
                <EpletTab 
                  epletData={responseData.eplets_found} 
                  donors={responseData.donors} 
                  recipients={responseData.recipients} 
                  classesToShow={classesToShow} 
                  tabState={EpletTabData} 
                  setTabState={setEpletTabData}
                  entityInfo={entityInfo}
                  useExon4={useExon4}
                />
              </TabPanel>

              <TabPanel value={activeTab} index={5}>
                <RankingTab 
                  rankingData={responseData.ranking}
                  recipients={responseData.recipients}
                  classesToShow={classesToShow}
                  tabState={RankingTabData}
                  setTabState={setRankingTabData}
                  useExon4={useExon4}
                />
              </TabPanel>
              </div>
        )}

       
        <ContactSection />

      </div>
    </ThemeProvider>
  );
}


function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default AlternativeLayout;